import "./styles.css";
import { Game } from "./model/Game";
import { Piece } from "./model/Piece";
import {
  findPreferredLevel,
  getLevelPresentation,
  type LevelProgress,
  parseSaveData,
  recordBestScore,
  stringifySaveData,
} from "./Progress";
import type { ParsedLevel } from "./Utils/JsonReader";
import { loadBrowserLevels } from "./Utils/JsonReader";
import type {
  SolverHint,
  SolverPathStep,
  SolverRequest,
  SolverResult,
} from "./SolverMessages";

interface LevelCollection {
  file: string;
  label: string;
}

interface LevelChoice {
  id: string;
  collection: string;
  level: ParsedLevel;
}

interface DragState {
  element: HTMLButtonElement;
  pieceIndex: number;
  pointerId: number;
  anchorX: number;
  anchorY: number;
  originalX: number;
  originalY: number;
  cellWidth: number;
  cellHeight: number;
  hasMoved: boolean;
}

const COLLECTIONS: LevelCollection[] = [
  { file: "hrd-games.json", label: "Classiques HRD" },
  { file: "levels.json", label: "Progression" },
];
const SAVE_STORAGE_KEY = "klotski-progress";

type ElementConstructor<T extends HTMLElement> = new () => T;

function elementById<T extends HTMLElement>(
  id: string,
  elementType: ElementConstructor<T>,
): T {
  const element = document.getElementById(id);
  if (!(element instanceof elementType)) {
    throw new Error(`Missing or invalid #${id} in index.html`);
  }
  return element;
}

const board = elementById("board", HTMLDivElement);
const levelSelect = elementById("level-select", HTMLSelectElement);
const initialCount = elementById("initial-count", HTMLElement);
const playedCount = elementById("played-count", HTMLElement);
const remainingCount = elementById("remaining-count", HTMLElement);
const projectedCount = elementById("projected-count", HTMLElement);
const hintButton = elementById("hint-button", HTMLButtonElement);
const resetButton = elementById("reset-button", HTMLButtonElement);
const status = elementById("status", HTMLParagraphElement);

const exportSaveButton = elementById("export-save-button", HTMLButtonElement);
const importSaveButton = elementById("import-save-button", HTMLButtonElement);
const saveFileInput = elementById("save-file-input", HTMLInputElement);
const victoryDialog = elementById("victory-dialog", HTMLDialogElement);
const victorySummary = elementById("victory-summary", HTMLParagraphElement);
const nextLevelButton = elementById("next-level-button", HTMLButtonElement);
const closeVictoryButton = elementById(
  "close-victory-button",
  HTMLButtonElement,
);

let solver: Worker | null = null;

let choices: LevelChoice[] = [];
let selectedLevel: ParsedLevel | null = null;
let game: Game | null = null;
let playedActions = 0;
let remainingActions: number | null = null;
let lastPieceIndex: number | null = null;
let hint: SolverHint | null = null;
let hintVisible = false;
let solutionPath: SolverPathStep[] = [];
let solving = false;
let latestRequestId = 0;
let drag: DragState | null = null;
let progress = loadLocalProgress();

function cloneGame(source: Game): Game {
  return new Game(
    source.pieces.map((piece) => piece.clone()),
    source.width,
    source.height,
  );
}

function setStatus(message: string): void {
  status.textContent = message;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function loadLocalProgress(): LevelProgress {
  try {
    const saved = localStorage.getItem(SAVE_STORAGE_KEY);
    return saved === null
      ? new Map<string, number>()
      : parseSaveData(JSON.parse(saved));
  } catch (error: unknown) {
    console.error(
      `Impossible de charger la progression : ${errorMessage(error)}`,
    );
    return new Map<string, number>();
  }
}

function persistLocalProgress(): void {
  localStorage.setItem(SAVE_STORAGE_KEY, stringifySaveData(progress));
}

function currentChoice(): LevelChoice | undefined {
  return choices.find((choice) => choice.id === levelSelect.value);
}

function updateLevelOption(
  option: HTMLOptionElement,
  choice: LevelChoice,
): void {
  const presentation = getLevelPresentation(choice, progress);
  option.textContent = presentation.label;
  option.className = presentation.className;
}

function refreshLevelOptions(): void {
  for (const option of Array.from(levelSelect.options)) {
    const choice = choices.find((item) => item.id === option.value);
    if (choice !== undefined) {
      updateLevelOption(option, choice);
    }
  }
}

function markCurrentLevelSolved(): void {
  const choice = currentChoice();
  if (choice === undefined) return;

  if (!recordBestScore(progress, choice.id, playedActions)) return;
  refreshLevelOptions();
  try {
    persistLocalProgress();
  } catch (error: unknown) {
    console.error(
      `Impossible de sauvegarder la progression : ${errorMessage(error)}`,
    );
  }
}

function preferredChoice(): LevelChoice | undefined {
  return findPreferredLevel(choices, progress);
}

function showVictoryScreen(): void {
  const choice = currentChoice();
  if (choice === undefined) return;

  markCurrentLevelSolved();
  const actionLabel = playedActions === 1 ? "action" : "actions";
  const target =
    selectedLevel?.minimumMoves === null || selectedLevel === null
      ? ""
      : ` Objectif initial : ${selectedLevel.minimumMoves}.`;
  victorySummary.textContent = `Niveau résolu en ${playedActions} ${actionLabel}.${target}`;

  const currentIndex = choices.findIndex((item) => item.id === choice.id);
  const hasNextLevel = choices[currentIndex + 1] !== undefined;
  nextLevelButton.disabled = !hasNextLevel;
  nextLevelButton.textContent = hasNextLevel
    ? "Niveau suivant"
    : "Dernier niveau";

  if (!victoryDialog.open) {
    victoryDialog.showModal();
  }
}

function updateCounters(): void {
  initialCount.textContent =
    selectedLevel?.minimumMoves === null || selectedLevel === null
      ? "—"
      : String(selectedLevel.minimumMoves);
  playedCount.textContent = String(playedActions);
  remainingCount.textContent = solving
    ? "…"
    : remainingActions === null
      ? "—"
      : String(remainingActions);
  projectedCount.textContent =
    solving || remainingActions === null
      ? "—"
      : String(playedActions + remainingActions);
}

function updateSolverDisplay(): void {
  hintButton.disabled = hint === null;
  updateCounters();
  renderBoard();

  if (remainingActions === null) {
    setStatus("Aucune solution n’existe depuis cette position.");
  } else if (remainingActions === 0) {
    setStatus("Niveau résolu !");
  } else {
    setStatus(
      `${remainingActions} action${remainingActions > 1 ? "s" : ""} au minimum depuis cette position.`,
    );
  }
}

function matchesCurrentGame(step: SolverPathStep): boolean {
  return (
    game !== null &&
    step.lastPieceIndex === lastPieceIndex &&
    step.pieces.length === game.pieces.length &&
    step.pieces.every((piece, index) => {
      const currentPiece = game?.pieces[index];
      return (
        currentPiece !== undefined &&
        currentPiece.position.x === piece.x &&
        currentPiece.position.y === piece.y
      );
    })
  );
}

function reuseSolutionPath(): boolean {
  const step = solutionPath.find(matchesCurrentGame);
  if (step === undefined) return false;

  solving = false;
  remainingActions = step.remainingActions;
  hint = step.hint;
  updateSolverDisplay();
  return true;
}

function arrowForHint(value: SolverHint): string {
  if (value.deltaX < 0) return "←";
  if (value.deltaX > 0) return "→";
  if (value.deltaY < 0) return "↑";
  return "↓";
}

function renderBoard(): void {
  board.replaceChildren();
  if (game === null) return;
  const currentGame = game;

  board.style.setProperty("--board-width", String(currentGame.width));
  board.style.setProperty("--board-height", String(currentGame.height));
  board.style.aspectRatio = `${currentGame.width} / ${currentGame.height}`;

  if (selectedLevel !== null) {
    const mainPiece = currentGame.pieces.find((piece) => piece.isMain);
    if (mainPiece !== undefined) {
      const targetWidth =
        Math.max(...mainPiece.positions.map((position) => position.x)) -
        mainPiece.position.x +
        1;
      const targetHeight =
        Math.max(...mainPiece.positions.map((position) => position.y)) -
        mainPiece.position.y +
        1;
      const target = document.createElement("div");
      target.className = "escape-target";
      target.setAttribute("role", "img");
      target.setAttribute(
        "aria-label",
        "Position cible de la pièce principale",
      );
      target.style.left = `${(selectedLevel.escapePoint.x / currentGame.width) * 100}%`;
      target.style.top = `${(selectedLevel.escapePoint.y / currentGame.height) * 100}%`;
      target.style.width = `${(targetWidth / currentGame.width) * 100}%`;
      target.style.height = `${(targetHeight / currentGame.height) * 100}%`;
      board.append(target);
    }
  }

  currentGame.pieces.forEach((piece, pieceIndex) => {
    const width =
      Math.max(...piece.positions.map((position) => position.x)) -
      piece.position.x +
      1;
    const height =
      Math.max(...piece.positions.map((position) => position.y)) -
      piece.position.y +
      1;
    const pieceElement = document.createElement("button");
    pieceElement.type = "button";
    pieceElement.className = `piece${piece.isMain ? " main" : ""}${
      hintVisible && hint?.pieceIndex === pieceIndex ? " hinted" : ""
    }`;
    pieceElement.style.left = `${(piece.position.x / currentGame.width) * 100}%`;
    pieceElement.style.top = `${(piece.position.y / currentGame.height) * 100}%`;
    pieceElement.style.width = `${(width / currentGame.width) * 100}%`;
    pieceElement.style.height = `${(height / currentGame.height) * 100}%`;
    pieceElement.setAttribute(
      "aria-label",
      `${piece.isMain ? "Pièce principale" : "Pièce"} ligne ${
        piece.position.y + 1
      }, colonne ${piece.position.x + 1}`,
    );

    const symbol = document.createElement("span");
    symbol.className = "piece-symbol";
    symbol.textContent = piece.isMain ? "曹" : "";
    pieceElement.append(symbol);

    const arrow = document.createElement("span");
    arrow.className = "hint-arrow";
    arrow.textContent =
      hintVisible && hint?.pieceIndex === pieceIndex ? arrowForHint(hint) : "";
    pieceElement.append(arrow);

    pieceElement.addEventListener("pointerdown", (event) => {
      beginDrag(event, pieceElement, pieceIndex);
    });
    board.append(pieceElement);
  });
}

function beginDrag(
  event: PointerEvent,
  element: HTMLButtonElement,
  pieceIndex: number,
): void {
  if (game === null) return;
  const piece = game.pieces[pieceIndex];
  if (piece === undefined) return;

  event.preventDefault();
  element.setPointerCapture(event.pointerId);
  element.classList.add("dragging");
  drag = {
    element,
    pieceIndex,
    pointerId: event.pointerId,
    anchorX: event.clientX,
    anchorY: event.clientY,
    originalX: piece.position.x,
    originalY: piece.position.y,
    cellWidth: board.clientWidth / game.width,
    cellHeight: board.clientHeight / game.height,
    hasMoved: false,
  };
  element.addEventListener("pointermove", moveDrag);
  element.addEventListener("pointerup", endDrag);
  element.addEventListener("pointercancel", cancelDrag);
}

function moveDrag(event: PointerEvent): void {
  if (drag === null || event.pointerId !== drag.pointerId || game === null) {
    return;
  }

  const activeDrag = drag;
  const piece = game.pieces[activeDrag.pieceIndex];
  if (piece === undefined) return;

  const maximumSteps = game.width + game.height;
  for (let attempt = 0; attempt < maximumSteps; attempt += 1) {
    const deltaX = event.clientX - activeDrag.anchorX;
    const deltaY = event.clientY - activeDrag.anchorY;
    const horizontalDistance = Math.abs(deltaX) / activeDrag.cellWidth;
    const verticalDistance = Math.abs(deltaY) / activeDrag.cellHeight;

    if (Math.max(horizontalDistance, verticalDistance) < 0.45) break;

    const horizontalFirst = horizontalDistance >= verticalDistance;
    const candidates: [number, number][] = horizontalFirst
      ? [
          [Math.sign(deltaX), 0],
          [0, Math.sign(deltaY)],
        ]
      : [
          [0, Math.sign(deltaY)],
          [Math.sign(deltaX), 0],
        ];

    let moved = false;
    for (const [stepX, stepY] of candidates) {
      const relevantDistance =
        stepX === 0 ? verticalDistance : horizontalDistance;
      if (
        relevantDistance < 0.45 ||
        (stepX === 0 && stepY === 0) ||
        !game.canMoveTo(piece, stepX, stepY)
      ) {
        continue;
      }

      piece.move(stepX, stepY);
      activeDrag.anchorX += stepX * activeDrag.cellWidth;
      activeDrag.anchorY += stepY * activeDrag.cellHeight;
      activeDrag.hasMoved = true;
      positionDraggedElement(activeDrag.element, piece, game);
      moved = true;
      break;
    }

    if (!moved) break;
  }
}

function positionDraggedElement(
  element: HTMLButtonElement,
  piece: Piece,
  currentGame: Game,
): void {
  element.style.left = `${(piece.position.x / currentGame.width) * 100}%`;
  element.style.top = `${(piece.position.y / currentGame.height) * 100}%`;
}

function clearDragListeners(): void {
  if (drag === null) return;
  drag.element.removeEventListener("pointermove", moveDrag);
  drag.element.removeEventListener("pointerup", endDrag);
  drag.element.removeEventListener("pointercancel", cancelDrag);
  drag.element.classList.remove("dragging");
}

function cancelDrag(event: PointerEvent): void {
  if (drag === null || event.pointerId !== drag.pointerId || game === null) {
    return;
  }

  const activeDrag = drag;
  const piece = game.pieces[activeDrag.pieceIndex];
  if (piece !== undefined && activeDrag.hasMoved) {
    piece.move(
      activeDrag.originalX - piece.position.x,
      activeDrag.originalY - piece.position.y,
    );
  }
  clearDragListeners();
  drag = null;
  renderBoard();
}

function endDrag(event: PointerEvent): void {
  if (drag === null || event.pointerId !== drag.pointerId || game === null) {
    return;
  }

  const activeDrag = drag;
  clearDragListeners();
  drag = null;

  if (!activeDrag.hasMoved) {
    renderBoard();
    return;
  }

  if (lastPieceIndex !== activeDrag.pieceIndex) {
    playedActions += 1;
  }
  lastPieceIndex = activeDrag.pieceIndex;
  hint = null;
  renderBoard();

  if (
    selectedLevel !== null &&
    game.isWinningGame(selectedLevel.escapePoint.x, selectedLevel.escapePoint.y)
  ) {
    solver?.terminate();
    solver = null;
    latestRequestId += 1;
    remainingActions = 0;
    solving = false;
    solutionPath = [];
    updateCounters();
    setStatus("Niveau résolu ! La pièce principale est libre.");
    hintButton.disabled = true;
    showVictoryScreen();
    return;
  }

  if (reuseSolutionPath()) return;

  void solvePosition();
}

function serializeGame(requestId: number): SolverRequest {
  if (game === null || selectedLevel === null) {
    throw new Error("Cannot solve a game before selecting a level");
  }
  return {
    requestId,
    width: game.width,
    height: game.height,
    pieces: game.pieces.map((piece) => ({
      x: piece.position.x,
      y: piece.position.y,
      pieceType: piece.pieceType,
      isMain: piece.isMain,
    })),
    lastPieceIndex,
    targetX: selectedLevel.escapePoint.x,
    targetY: selectedLevel.escapePoint.y,
  };
}

async function solvePosition(): Promise<void> {
  if (game === null || selectedLevel === null) return;
  solutionPath = [];
  solving = true;
  remainingActions = null;
  hint = null;
  hintButton.disabled = true;
  latestRequestId += 1;
  updateCounters();
  setStatus("Le solveur analyse cette position…");

  solver?.terminate();
  solver = new Worker(new URL("./solver.worker.ts", import.meta.url), {
    type: "module",
  });
  solver.addEventListener("message", handleSolverResult);
  solver.addEventListener("error", handleSolverError);
  solver.postMessage(serializeGame(latestRequestId));
}

function handleSolverResult(event: MessageEvent<SolverResult>): void {
  if (event.data.requestId !== latestRequestId) return;
  solutionPath = event.data.path;
  const firstStep = solutionPath[0];
  solving = false;
  remainingActions = firstStep?.remainingActions ?? null;
  hint = firstStep?.hint ?? null;
  updateSolverDisplay();
}

function handleSolverError(event: ErrorEvent): void {
  solutionPath = [];
  solving = false;
  hintButton.disabled = true;
  updateCounters();
  setStatus(`Le solveur a échoué : ${event.message}`);
}

function selectLevel(choice: LevelChoice): void {
  if (victoryDialog.open) victoryDialog.close();
  selectedLevel = choice.level;
  game = cloneGame(choice.level.game);
  solutionPath = [];
  playedActions = 0;
  remainingActions = choice.level.minimumMoves;
  lastPieceIndex = null;
  hint = null;
  resetButton.disabled = false;
  hintButton.disabled = true;
  renderBoard();
  updateCounters();
  void solvePosition();
}

function populateLevelSelect(): void {
  levelSelect.replaceChildren();
  for (const collection of COLLECTIONS) {
    const groupChoices = choices.filter(
      (choice) => choice.collection === collection.file,
    );
    if (groupChoices.length === 0) continue;

    const group = document.createElement("optgroup");
    group.label = collection.label;
    for (const choice of groupChoices) {
      const option = document.createElement("option");
      option.value = choice.id;
      updateLevelOption(option, choice);
      group.append(option);
    }
    levelSelect.append(group);
  }
  levelSelect.disabled = choices.length === 0;
}

function exportProgress(): void {
  const blob = new Blob([stringifySaveData(progress)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "klotski-save.json";
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
  setStatus("Sauvegarde exportée.");
}

async function importProgress(file: File): Promise<void> {
  try {
    const importedProgress = parseSaveData(JSON.parse(await file.text()));
    localStorage.setItem(SAVE_STORAGE_KEY, stringifySaveData(importedProgress));
    progress = importedProgress;
    refreshLevelOptions();
    const nextChoice = preferredChoice();
    if (nextChoice !== undefined) {
      levelSelect.value = nextChoice.id;
      selectLevel(nextChoice);
    }

    const count = progress.size;
    setStatus(
      `Sauvegarde importée : ${count} niveau${count > 1 ? "x" : ""} résolu${count > 1 ? "s" : ""}.`,
    );
  } catch (error: unknown) {
    console.error(
      `Impossible d’importer la sauvegarde : ${errorMessage(error)}`,
    );
    setStatus("Le fichier de sauvegarde est invalide ou inaccessible.");
  }
}

levelSelect.addEventListener("change", () => {
  const choice = choices.find((item) => item.id === levelSelect.value);
  if (choice !== undefined) selectLevel(choice);
});

resetButton.addEventListener("click", () => {
  const choice = choices.find((item) => item.id === levelSelect.value);
  if (choice !== undefined) selectLevel(choice);
});

hintButton.addEventListener("click", () => {
  hintVisible = !hintVisible;
  hintButton.textContent = hintVisible
    ? "Masquer l’indice"
    : "Afficher l’indice";
  renderBoard();
});

exportSaveButton.addEventListener("click", exportProgress);

importSaveButton.addEventListener("click", () => {
  saveFileInput.click();
});

saveFileInput.addEventListener("change", () => {
  const file = saveFileInput.files?.[0];
  saveFileInput.value = "";
  if (file !== undefined) {
    void importProgress(file);
  }
});

nextLevelButton.addEventListener("click", () => {
  const choice = currentChoice();
  if (choice === undefined) return;

  const currentIndex = choices.findIndex((item) => item.id === choice.id);
  const nextChoice = choices[currentIndex + 1];
  if (nextChoice === undefined) return;

  levelSelect.value = nextChoice.id;
  selectLevel(nextChoice);
});

closeVictoryButton.addEventListener("click", () => {
  victoryDialog.close();
});

async function start(): Promise<void> {
  const collections = await Promise.all(
    COLLECTIONS.map(async (collection) => ({
      collection,
      levels: await loadBrowserLevels(collection.file),
    })),
  );

  choices = collections.flatMap(({ collection, levels }) =>
    levels.map((level) => ({
      id: `${collection.file}:${level.level}`,
      collection: collection.file,
      level,
    })),
  );

  populateLevelSelect();
  const nextChoice = preferredChoice();
  if (nextChoice === undefined) {
    setStatus("Aucun niveau valide n’a pu être chargé.");
    return;
  }
  levelSelect.value = nextChoice.id;
  selectLevel(nextChoice);
}

void start();
